import React, { useState, useEffect } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, CalendarIcon, User, Mail, MessageSquare, Clock } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from '@/lib/utils';
import BookingConfirmation from '@/components/BookingConfirmation';
import { 
  saveBooking, 
  isTimeSlotAvailable, 
  getAvailableTimeSlots, 
  sendEmailNotification,
  BookingData
} from '@/services/bookingService';
import PageHeader from '@/components/PageHeader';

// Available time slots
const allTimeSlots = [
  "9:00 AM", "10:00 AM", "11:00 AM", 
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"
];

// Form schema with validation
const formSchema = z.object({
  name: z.string().min(2, { message: "Name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
  businessType: z.string().min(2, { message: "Business type is required" }),
  serviceType: z.enum(["fixed", "subscription"], {
    required_error: "Please select a service type",
  }),
  message: z.string().min(10, { message: "Message should be at least 10 characters" }),
  date: z.date({
    required_error: "Please select a date for your consultation",
  }),
  timeSlot: z.string({
    required_error: "Please select a time for your consultation",
  }),
  agreeToTerms: z.boolean().refine((val) => val === true, {
    message: "You must agree to the terms and conditions",
  }),
});

const BookingPage = () => {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | undefined>(undefined);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>(allTimeSlots);
  const [submitting, setSubmitting] = useState(false);
  const [completedBooking, setCompletedBooking] = useState<BookingData | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      businessType: "",
      serviceType: "fixed",
      message: "",
      agreeToTerms: false,
    },
  });

  // Update available time slots when date changes
  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (date) {
        const available = await getAvailableTimeSlots(date, allTimeSlots);
        setAvailableTimeSlots(available);
        
        // Clear selected time slot if it's no longer available
        if (selectedTimeSlot && !available.includes(selectedTimeSlot)) {
          setSelectedTimeSlot(undefined);
          form.setValue("timeSlot", "");
        }
      }
    };
    
    fetchAvailableSlots();
  }, [date, form, selectedTimeSlot]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setSubmitting(true);
    
    try {
      // Final availability check before submission
      const isAvailable = await isTimeSlotAvailable(values.date, values.timeSlot);
      if (!isAvailable) {
        toast.error("This time slot is no longer available. Please select another time.");
        setSubmitting(false);
        return;
      }
      
      // Save the booking - ensuring we have all required non-optional fields
      const bookingData = {
        name: values.name,
        email: values.email,
        businessType: values.businessType,
        serviceType: values.serviceType,
        message: values.message,
        date: values.date,
        timeSlot: values.timeSlot
      };
      
      const booking = await saveBooking(bookingData);
      
      // Send email notification
      const emailSent = await sendEmailNotification(booking);
      
      if (!emailSent) {
        toast.warning("Booking saved, but we couldn't send confirmation emails.", {
          description: "Our team will still receive your booking and contact you.",
        });
      } else {
        // Show success toast
        toast.success("Booking request submitted", {
          description: `Your consultation is scheduled for ${format(values.date, "MMMM d, yyyy")} at ${values.timeSlot}.`,
        });
      }
      
      // Set the completed booking to show confirmation
      setCompletedBooking(booking);
      
      // Reset form
      form.reset();
      setDate(undefined);
      setSelectedTimeSlot(undefined);
    } catch (error) {
      console.error("Booking submission failed:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    setCompletedBooking(null);
  };

  if (completedBooking) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        <main className="flex-1 py-20 px-6 md:px-10">
          <div className="container mx-auto max-w-6xl">
            <BookingConfirmation booking={completedBooking} onDone={handleDone} />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <main className="flex-1">
        <PageHeader
          title={<>Let's Create Something <span className="text-gold">Beautiful</span> Together.</>}
          subtitle="A 15-minute call could change your business forever. Let's chat, and we'll show you how to get a high-end web app without the high-end price tag."
        />
        
        <section className="py-20 px-6 md:px-10 -mt-10">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-10 items-start">
              {/* Booking Form Section */}
              <div className="rounded-2xl border border-border p-8 bg-card">
                <h2 className="text-2xl font-semibold mb-6">Book Your Free Consultation</h2>
                
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Your Name
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="John Doe" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            Email Address
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="you@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="businessType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            Business Type
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="E-commerce, SaaS, Agency, etc." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="serviceType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Preferred Service</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a service type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="fixed">Fixed Project</SelectItem>
                              <SelectItem value="subscription">Subscription</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <MessageSquare className="h-4 w-4" />
                            Your Message
                          </FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Tell us about your project and what you're looking to achieve..." 
                              className="min-h-[120px]" 
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="date"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Consultation Date</FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant={"outline"}
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(field.value, "MMMM d, yyyy")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={field.value}
                                  onSelect={(date) => {
                                    field.onChange(date);
                                    setDate(date);
                                  }}
                                  disabled={(date) =>
                                    date < new Date(new Date().setHours(0, 0, 0, 0)) ||
                                    date.getDay() === 0 ||
                                    date.getDay() === 6
                                  }
                                  initialFocus
                                  className="p-3 pointer-events-auto"
                                />
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="timeSlot"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              Time Slot
                            </FormLabel>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(value);
                                setSelectedTimeSlot(value);
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a time" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableTimeSlots.length > 0 ? (
                                  availableTimeSlots.map((slot) => (
                                    <SelectItem key={slot} value={slot}>
                                      {slot}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="" disabled>
                                    No available slots
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="agreeToTerms"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-sm font-normal">
                              I agree to the terms and privacy policy
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>Processing...</>
                      ) : (
                        <>
                          <Check className="mr-2 h-4 w-4" />
                          Book Your Free Consultation
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              </div>

              {/* Calendar Section */}
              <div className="rounded-2xl border border-border overflow-hidden p-6 bg-card">
                <div className="mb-4">
                  <h2 className="text-2xl font-semibold mb-2">Select Your Preferred Time</h2>
                  <p className="text-muted-foreground">
                    Choose a date and time that works for you. Our team is available Monday-Friday.
                  </p>
                </div>
                
                <div className="rounded-lg border border-border p-4 bg-card/50 mb-6">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) =>
                      date < new Date(new Date().setHours(0, 0, 0, 0)) ||
                      date.getDay() === 0 ||
                      date.getDay() === 6
                    }
                    className="mx-auto p-3 pointer-events-auto"
                  />
                </div>

                {date && (
                  <div>
                    <h3 className="font-medium mb-2">Available Time Slots for {format(date, "MMMM d, yyyy")}</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {availableTimeSlots.length > 0 ? (
                        availableTimeSlots.map((slot) => (
                          <Button
                            key={slot}
                            variant={selectedTimeSlot === slot ? "default" : "outline"}
                            size="sm"
                            className="w-full"
                            onClick={() => {
                              setSelectedTimeSlot(slot);
                              form.setValue("timeSlot", slot);
                              form.setValue("date", date);
                            }}
                          >
                            {slot}
                          </Button>
                        ))
                      ) : (
                        <p className="col-span-2 text-center py-2 text-muted-foreground">
                          No available slots for this date. Please select another date.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {date && selectedTimeSlot && (
                  <div className="mt-6 p-4 bg-primary/10 rounded-lg">
                    <h3 className="font-medium mb-2">Your Selected Appointment</h3>
                    <p className="text-sm mb-1">
                      <strong>Date:</strong> {format(date, "MMMM d, yyyy")}
                    </p>
                    <p className="text-sm mb-1">
                      <strong>Time:</strong> {selectedTimeSlot}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Fill out the form on the left to confirm your booking.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default BookingPage;
